import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Navbar } from '../../components/layout/Navbar';
import { Footer } from '../../components/layout/Footer';
import { usePageMeta } from '../../lib/usePageMeta';
import type { BlogPostMeta } from './types';

/**
 * Renders the post's hero image with a graceful fallback. If the image file
 * is missing or the path is empty, drops back to a gradient + emoji placeholder
 * so unfinished posts still ship.
 */
function CoverImage({ src, alt }: { src: string; alt: string }) {
  const [errored, setErrored] = useState(false);
  const showImage = Boolean(src) && !errored;

  return (
    <div
      className="rounded-xl border border-border overflow-hidden mb-10 h-64 sm:h-80 relative"
      style={{ background: 'linear-gradient(135deg, #0d0d12 0%, #1a1a2e 100%)' }}
    >
      {showImage ? (
        <img
          src={src}
          alt={alt}
          className="absolute inset-0 w-full h-full object-cover"
          loading="eager"
          decoding="async"
          onError={() => setErrored(true)}
          itemProp="image"
        />
      ) : (
        <div
          className="absolute inset-0 flex items-center justify-center text-7xl"
          role="img"
          aria-label={alt}
        >
          <span aria-hidden="true">📚</span>
          <meta itemProp="image" content={src} />
        </div>
      )}
    </div>
  );
}

export interface HowToStep {
  name: string;
  text: string;
}
export interface FaqItem {
  question: string;
  answer: string;
}

interface BlogPostLayoutProps {
  meta: BlogPostMeta;
  /** Optional HowTo steps — included in JSON-LD @graph for rich results. */
  howTo?: { name: string; totalTime?: string; steps: HowToStep[] };
  /** Optional FAQ items — included in JSON-LD @graph for rich results. */
  faq?: FaqItem[];
  children: React.ReactNode;
}

/**
 * Shared shell for every blog post: Navbar, breadcrumbs, article header
 * (category eyebrow + h1 + description + author byline + reading time),
 * cover slot, the post body (as `children`), back-link footer, site Footer.
 *
 * Also wires document head — title, meta description, canonical, OG,
 * Twitter, article:* meta, JSON-LD Article schema — from `meta`.
 *
 * Post authors only write the body. The shell is consistent across all posts.
 */
export function BlogPostLayout({ meta, howTo, faq, children }: BlogPostLayoutProps) {
  const publishedISO = `${meta.publishedAt}T09:00:00Z`;

  // Build the JSON-LD @graph: Article is always present; HowTo and FAQPage
  // are appended when provided. Google honors multiple @types via @graph and
  // surfaces matching rich results per type.
  const articleNode: Record<string, unknown> = {
    '@type': 'Article',
    '@id': `${meta.canonicalUrl}#article`,
    headline: meta.title,
    description: meta.description,
    image: [meta.coverImage],
    datePublished: publishedISO,
    dateModified: publishedISO,
    author: {
      '@type': 'Person',
      name: meta.author.name,
      jobTitle: meta.author.role,
    },
    publisher: {
      '@type': 'Organization',
      name: 'StoryMint',
      logo: { '@type': 'ImageObject', url: 'https://storymint.io/logo.png' },
    },
    mainEntityOfPage: { '@type': 'WebPage', '@id': meta.canonicalUrl },
    articleSection: meta.category,
    keywords: meta.tags.join(', '),
  };

  const graph: Array<Record<string, unknown>> = [articleNode];

  if (howTo) {
    graph.push({
      '@type': 'HowTo',
      '@id': `${meta.canonicalUrl}#howto`,
      name: howTo.name,
      ...(howTo.totalTime ? { totalTime: howTo.totalTime } : {}),
      step: howTo.steps.map((s, i) => ({
        '@type': 'HowToStep',
        position: i + 1,
        name: s.name,
        text: s.text,
      })),
    });
  }

  if (faq && faq.length > 0) {
    graph.push({
      '@type': 'FAQPage',
      '@id': `${meta.canonicalUrl}#faq`,
      mainEntity: faq.map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: { '@type': 'Answer', text: item.answer },
      })),
    });
  }

  usePageMeta({
    title: `${meta.title} | StoryMint Blog`,
    description: meta.description,
    canonical: meta.canonicalUrl,
    ogType: 'article',
    ogImage: meta.coverImage.startsWith('http')
      ? meta.coverImage
      : `https://storymint.io${meta.coverImage}`,
    twitterCard: 'summary_large_image',
    articlePublishedTime: publishedISO,
    articleModifiedTime: publishedISO,
    articleAuthor: meta.author.name,
    articleSection: meta.category,
    articleTags: meta.tags,
    jsonLd: {
      '@context': 'https://schema.org',
      '@graph': graph,
    },
  });

  const publishedLabel = new Date(`${meta.publishedAt}T00:00:00Z`).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

  return (
    <div className="min-h-screen bg-background">
      <Navbar variant="public" />

      <article
        className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-16"
        itemScope
        itemType="https://schema.org/Article"
      >
        {/* Breadcrumbs */}
        <nav
          aria-label="Breadcrumb"
          className="mb-8 text-sm text-muted-foreground"
          itemScope
          itemType="https://schema.org/BreadcrumbList"
        >
          <ol className="flex flex-wrap items-center gap-2">
            <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
              <Link to="/" itemProp="item" className="hover:text-foreground transition-colors">
                <span itemProp="name">Home</span>
              </Link>
              <meta itemProp="position" content="1" />
            </li>
            <li aria-hidden="true">/</li>
            <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
              <Link to="/blog" itemProp="item" className="hover:text-foreground transition-colors">
                <span itemProp="name">Blog</span>
              </Link>
              <meta itemProp="position" content="2" />
            </li>
            <li aria-hidden="true">/</li>
            <li
              itemProp="itemListElement"
              itemScope
              itemType="https://schema.org/ListItem"
              className="text-foreground truncate max-w-[60vw]"
            >
              <span itemProp="name">{meta.title}</span>
              <meta itemProp="position" content="3" />
            </li>
          </ol>
        </nav>

        {/* Header */}
        <header className="mb-10">
          <div className="text-xs font-bold text-primary tracking-[0.12em] uppercase mb-3">
            <span itemProp="articleSection">{meta.category}</span>
          </div>
          <h1
            itemProp="headline"
            className="text-[clamp(2rem,5vw,3.25rem)] font-extrabold text-foreground mb-6 leading-[1.15] tracking-tight"
          >
            {meta.title}
          </h1>
          <p
            className="text-base sm:text-xl text-muted-foreground leading-[1.65] mb-6"
            itemProp="description"
          >
            {meta.description}
          </p>
          <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
            <address
              className="not-italic"
              itemProp="author"
              itemScope
              itemType="https://schema.org/Person"
            >
              By{' '}
              <span itemProp="name" className="text-foreground font-medium">
                {meta.author.name}
              </span>
              <span className="hidden sm:inline"> · </span>
              <span className="block sm:inline" itemProp="jobTitle">
                {meta.author.role}
              </span>
            </address>
            <span aria-hidden="true">·</span>
            <time dateTime={publishedISO} itemProp="datePublished">
              {publishedLabel}
            </time>
            <meta itemProp="dateModified" content={publishedISO} />
            <span aria-hidden="true">·</span>
            <span>{meta.readingTime}</span>
          </div>
        </header>

        {/* Cover */}
        <CoverImage src={meta.coverImage} alt={`Cover illustration for ${meta.title}`} />

        {/* Body */}
        <div
          className="prose-content space-y-6 text-foreground text-[17px] leading-[1.8]"
          itemProp="articleBody"
        >
          {children}
        </div>

        {/* Footer */}
        <footer className="mt-12 pt-8 border-t border-border">
          <Link
            to="/blog"
            className="text-sm font-semibold text-[#a78bfa] hover:text-foreground transition-colors"
          >
            ← Back to all posts
          </Link>
        </footer>
      </article>

      <Footer />
    </div>
  );
}
